import { AlignLeft, BuildingComplex, CalendarClock, Coins, Contact, Megaphone, Percent, TrendingUp, User } from 'lucide-react';
import { OpportunityRow } from 'bindings/opportunities/OpportunityRow';
import close from 'images/icons/orders/close.svg';
import open_new_tab from 'images/icons/company/open_new_tab.svg';
import { ITag } from 'proto/protobufs';
import * as React from 'react';
import { useRecoilValue } from 'recoil';
import { AUsers } from '../../../atoms/global/users';
import ClientCompany from '../../../components_v2/clientCompany/ClientCompany';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import DropdownOwners, { UserBlock } from '../../../components_v2/dropdown/DropdownOwners';
import TagsPicker from '../../../components_v2/dropdown/TagsPicker';
import Input, { blurOnEnterPressed } from '../../../components_v2/input/Input';
import Popup, { PopupContext } from '../../../components_v2/popup/Popup';
import { PopupMode } from '../../../components_v2/popup/model/Model';
import { getTags, updateTags } from '../../../components_v2/toolbarFilter/tags/action';
import { TagType } from '../../../components_v2/toolbarFilter/tags/Model';
import { DefaultImage } from '../../../styles/global/css/GlobalImage';
import { Open } from '../../../styles/global/css/Open';
import { SidelyBlack } from '../../../styles/global/css/Utils';
import ToolTip from '../../../styles/global/ToolTip';
import { Translate } from '../../../styles/global/translate';
import { DetailPopupHeader, DetailPopupLeftContainer } from '../../client-companies/popup/CompanyPopup';
import { DateAccessor } from '../../client-companies/data/CompanyColumns';
import { getCompanyContacts, getProspectOrigins } from '../../client-companies/data/action';
import { CompanyContact, ProspectOrigin } from '../../client-companies/model/Model';
import { BodyHeaderStatus, BodyMainContainer, BodyMainRow, BodyMainText, BodyMainTextEditable, Container, FullOpen, HeaderBlock, HeaderRight } from '../../client-companies/style/PopupStyle';
import { validateNumberAndEmpty } from '../../globals/FieldFunction';
import { Unhautorized } from '../../noData/NoData';
import Restricted from '../../permissions/Restricted';
import usePermission from '../../permissions/usePermission';
import { FlexDiv } from '../../products/style';
import { formatCurrency } from '../../reports/utils';
import { QueryUpdateOpportunity, getOpportunity, getOpportunityStatuses, updateOpportunity as rawUpdateOpportunity } from '../data/Data';
import { PROBABILITIES, ProbabilityBadge, statusDatalist } from '../data/OpportunityColumns';
import { OpportunityStatus } from '../model/Model';
import { DropdownStyle } from '../../../components_v2/dropdown/model/Model';

export default function OpportunityPopup(props: {
	isOpen: boolean
	setOpen: (value: boolean) => void
	opportunityId?: number
	// lets the list page keep its row in sync
	updateOpportunity?: (id: number, params: QueryUpdateOpportunity) => Promise<void>
	// fetched by the popup when the caller has none (e.g. company linked elements)
	tags?: ITag[]
}): JSX.Element {
	const [opportunity, setOpportunity] = React.useState<OpportunityRow>();
	const [statuses, setStatuses] = React.useState<OpportunityStatus[]>();
	const [ownTags, setOwnTags] = React.useState<ITag[]>([]);

	React.useEffect(() => {
		getOpportunityStatuses().then(setStatuses);
		if (!props.tags) getTags(TagType.OPPORTUNITY).then(setOwnTags);
	}, []);

	const fetchOpportunity = (id?: number) => id ? getOpportunity(id).then(setOpportunity) : setOpportunity(undefined);

	React.useEffect(() => {
		fetchOpportunity(props.opportunityId);
	}, [props.opportunityId]);

	const onClickOut = React.useCallback(() => props.setOpen(false), []);

	return <Popup
		isOpen={props.isOpen}
		onClickOut={onClickOut}
		popupStyle={{ animate: true, minWidth: '450px' }}
		popupMode={PopupMode.Details}
		content={opportunity && statuses
			? <OpportunityPopupContent
				opportunity={opportunity}
				statuses={statuses}
				tags={props.tags ?? ownTags}
				onClickOut={onClickOut}
				refresh={() => fetchOpportunity(opportunity.id)}
				updateOpportunity={params => (props.updateOpportunity ?? rawUpdateOpportunity)(opportunity.id, params)
					.then(() => fetchOpportunity(opportunity.id))
					.catch(console.error)}
			/>
			: undefined}
	/>;
}

function DetailRow(props: { icon: JSX.Element, translateId: string, children: React.ReactNode }) {
	return <BodyMainRow gap='20px'>
		<ToolTip toolTipContent={<Translate id={props.translateId} />} left='33%'>{props.icon}</ToolTip>
		{props.children}
	</BodyMainRow>;
}

function EditableText(props: { value: string | null, readOnly: boolean, isValid?: (s: string) => boolean, onChange: (value: string | null) => void }) {
	return <BodyMainTextEditable
		marginLeft='0'
		minWidth='100px'
		suppressContentEditableWarning
		contentEditable={!props.readOnly}
		onKeyDownCapture={blurOnEnterPressed}
		onBlur={e => {
			const value = e.currentTarget.innerText.trim();
			if (props.readOnly || value === (props.value ?? '')) return;
			if (props.isValid && !props.isValid(value)) {
				e.currentTarget.innerText = props.value ?? '';
				return;
			}
			props.onChange(value || null);
		}}
	>
		{props.value}
	</BodyMainTextEditable>;
}

function OpportunityPopupContent(props: {
	opportunity: OpportunityRow
	statuses: OpportunityStatus[]
	tags: ITag[]
	onClickOut: () => void
	refresh: () => void
	updateOpportunity: (params: QueryUpdateOpportunity) => Promise<void>
}) {
	const { opportunity: o, statuses, updateOpportunity } = props;
	const { isChild } = React.useContext(PopupContext);
	const users = useRecoilValue(AUsers);
	const canEdit = usePermission({ objectAction: 'UpdateOpportunity' }, { ownerId: o.owner_id });
	const [contacts, setContacts] = React.useState<CompanyContact[]>([]);
	const [prospectOrigins, setProspectOrigins] = React.useState<ProspectOrigin[]>([]);
	const [statusOpen, setStatusOpen] = React.useState(false);
	const [isCompanyOpen, setIsCompanyOpen] = React.useState(false);

	React.useEffect(() => {
		getProspectOrigins().then(setProspectOrigins);
	}, []);

	React.useEffect(() => {
		getCompanyContacts({ client_company_id: o.client_company_id, limit: 200 }).then(res => setContacts(res.contacts));
	}, [o.client_company_id]);

	const status = statuses.find(s => s.id === o.opportunity_status_id);
	const owner = users.find(u => u.id === o.owner_id);
	const contactOptions = contacts.map(c => ({ label: `${c.contact_last_name ?? ''} ${c.contact_first_name ?? ''}`.trim(), value: c.contact_id }));
	const originOptions = prospectOrigins.map(p => ({ label: p.name, value: p.id }));
	const dropdownStyle: DropdownStyle = { optionWidth: '300px', optionHeight: '150px', width: '100%', containerWidth: '100%' };

	return <Restricted
		to={{ objectAction: 'ReadOpportunity' }}
		ownerId={o.owner_id}
		fallback={<>
			<DefaultImage cursor="pointer" src={close} onClick={props.onClickOut} />
			<Unhautorized />
		</>}
	>
		<Container>
			<DetailPopupLeftContainer isFullOpen={false}>
				<DetailPopupHeader minHeight='100px'>
					<DefaultImage cursor="pointer" src={close} onClick={props.onClickOut} />
					<FlexDiv flow="column" margin="0 0 0 15px" align="flex-start" width='calc(100% - 50px)' gap='4px'>
						<HeaderBlock flexDirection="column">
							<BodyMainTextEditable
								marginLeft='0px'
								fontSize='15px'
								fontWeight='700'
								color={SidelyBlack}
								suppressContentEditableWarning
								contentEditable={canEdit}
								onKeyDownCapture={blurOnEnterPressed}
								minWidth='10px'
								onBlur={e => {
									const title = e.currentTarget.innerText.trim();
									if (canEdit && title !== (o.title ?? '')) updateOpportunity({ title: title || null });
								}}
							>
								{o.title}
							</BodyMainTextEditable>
						</HeaderBlock>
						<HeaderBlock flexDirection="row">
							{canEdit
								? <Dropdown
									dropdownStyle={{ optionWidth: '300px', height: '21px' }}
									name="select_status"
									datalist={statusDatalist(statuses)}
									JSXButton={() => <FlexDiv gap="5px">
										<BodyHeaderStatus backgroundColor={status?.status_color_code}>{status?.name ?? '-'}</BodyHeaderStatus>
										<Open isOpen={statusOpen} size={10} />
									</FlexDiv>}
									JSXContent={s => <BodyHeaderStatus inDropdown backgroundColor={s.color}>{s.label ?? '-'}</BodyHeaderStatus>}
									// as in the creation popup, picking a status applies its probability
									onChange={v => updateOpportunity({ opportunity_status_id: v.value.id, probability: v.value.probability })}
									onOpen={setStatusOpen}
								/>
								: <BodyHeaderStatus backgroundColor={status?.status_color_code}>{status?.name ?? '-'}</BodyHeaderStatus>}
						</HeaderBlock>
					</FlexDiv>
					<HeaderRight>
						{isChild && <FullOpen isActive src={open_new_tab} onClick={() => window.open(`/opportunities?id=${o.id}`, '_blank')?.focus()} />}
					</HeaderRight>
				</DetailPopupHeader>
				<FlexDiv flow="column" width='100%' padding='15px 0 0 0'>
					<TagsPicker
						readOnly={!canEdit}
						paddingLeft='10px'
						tags={props.tags}
						selected={props.tags.filter(t => o.tag_ids.includes(t.id!))}
						onAdd={tag => updateTags(TagType.OPPORTUNITY, { ressource_id: o.id, ids_add: [tag.id!] }).then(props.refresh)}
						onDelete={id => updateTags(TagType.OPPORTUNITY, { ressource_id: o.id, ids_delete: [id] }).then(props.refresh)}
					/>
					<BodyMainContainer noScroll noHeight>
						<DetailRow icon={<User />} translateId='owner'>
							{canEdit
								? <DropdownOwners
									dropdownStyle={dropdownStyle}
									users={users.map(u => ({ label: u.name, value: u, image: u.photoUrl }))}
									selected={owner ? { label: owner.name, value: owner, image: owner.photoUrl } : undefined}
									onChange={v => v.value && updateOpportunity({ owner_id: v.value.id })}
									open
								/>
								: <UserBlock user={owner} />}
						</DetailRow>
						<DetailRow icon={<BuildingComplex />} translateId='client_company_name'>
							<BodyMainText marginLeft='0' cursor='pointer' onClick={() => setIsCompanyOpen(true)}>{o.client_company_name}</BodyMainText>
						</DetailRow>
						<DetailRow icon={<Contact />} translateId='company.creation.opportunity.contact'>
							<Dropdown
								disabled={!canEdit}
								dropdownStyle={dropdownStyle}
								name='dropdown_contact'
								readOnly
								cancellable
								datalist={contactOptions}
								selectedValue={contactOptions.find(c => c.value === o.contact_id)}
								onChange={v => updateOpportunity({ contact_id: v?.value ?? null })}
							/>
						</DetailRow>
						<DetailRow icon={<CalendarClock />} translateId='deadline'>
							{/* column flow so the inline calendar opens under the input, not beside it */}
							<FlexDiv flow='column' align='flex-start'>
								<DateAccessor
									input={canEdit}
									date={o.deadline}
									onDateChange={d => updateOpportunity({ deadline: d.toISOString() })}
									pickerProps={{ left: '50%', noAbsolute: false, transform: 'translate(-50%, 40px)' }}
								/>
							</FlexDiv>
						</DetailRow>
						<DetailRow icon={<Coins />} translateId='amount'>
							<EditableText readOnly={!canEdit} value={o.amount?.toString() ?? null} isValid={validateNumberAndEmpty} onChange={v => updateOpportunity({ amount: v === null ? null : Number(v) })} />
						</DetailRow>
						<DetailRow icon={<Percent />} translateId='probability'>
							{canEdit
								? <Dropdown
									dropdownStyle={{ ...dropdownStyle, optionWidth: '100px', width: 'fit-content', containerWidth: 'fit-content' }}
									name='dropdown_probability'
									readOnly
									datalist={PROBABILITIES}
									JSXButton={() => <ProbabilityBadge probability={o.probability} />}
									JSXContent={p => <ProbabilityBadge inDropdown probability={p.value} />}
									onChange={v => updateOpportunity({ probability: v.value })}
								/>
								: <ProbabilityBadge probability={o.probability} />}
						</DetailRow>
						<DetailRow icon={<TrendingUp />} translateId='revenue'>
							<BodyMainText marginLeft='0'>{formatCurrency(o.revenue)}</BodyMainText>
						</DetailRow>
						<DetailRow icon={<Megaphone />} translateId='company.creation.opportunity.prospect_origin'>
							<Dropdown
								disabled={!canEdit}
								dropdownStyle={dropdownStyle}
								name='dropdown_prospect_origin'
								readOnly
								cancellable
								datalist={originOptions}
								selectedValue={originOptions.find(p => p.value === o.prospect_origin)}
								onChange={v => updateOpportunity({ prospect_origin: v?.value ?? null })}
							/>
						</DetailRow>
						<DetailRow icon={<AlignLeft />} translateId='description'>
							<Input
								textArea
								disabled={!canEdit}
								name='opportunity_description'
								type='text'
								placeholder='-'
								value={o.description ?? ''}
								inputStyle={{ containerWidth: '100%', width: '100%', height: '100px', padding: '5px' }}
								onBlur={e => {
									const value = e.currentTarget.value.trim();
									if (value !== (o.description ?? '')) updateOpportunity({ description: value || null });
								}}
							/>
						</DetailRow>
					</BodyMainContainer>
				</FlexDiv>
			</DetailPopupLeftContainer>
		</Container>
		<ClientCompany isOpen={isCompanyOpen} setOpen={setIsCompanyOpen} clientCompanyId={o.client_company_id} />
	</Restricted>;
}
